import { router } from "@inertiajs/react"
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, Trash2Icon, Edit2Icon } from "lucide-react"
import { type ReactNode, useEffect, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { AdminLayout } from "@/layouts/AdminLayout"
import { PoiIcon } from "@/lib/poi-icon"
import type { InsideCategoryItem, InsideItem, RenderType } from "@/lib/types"
import { ItemForm } from "./ItemForm"

interface PropertySummary {
  id: number
  name: string
  region_id: number | null
}

interface Props {
  property: PropertySummary
  categories: InsideCategoryItem[]
  items: InsideItem[]
  selected_category_id: number | null
  selected_render_type: RenderType | null
  urls: { index: string; reorder: string; create: string }
}

function swapAdjacent<T extends { id: number }>(items: T[], id: number, direction: "up" | "down"): T[] {
  const idx = items.findIndex((i) => i.id === id)
  if (idx === -1) return items
  if (direction === "up" && idx === 0) return items
  if (direction === "down" && idx === items.length - 1) return items
  const next = [...items]
  const swap = direction === "up" ? idx - 1 : idx + 1
  ;[next[idx], next[swap]] = [next[swap], next[idx]]
  return next
}

interface ItemCardProps {
  item: InsideItem
  isFirst: boolean
  isLast: boolean
  onEdit: (item: InsideItem) => void
  onDelete: (item: InsideItem) => void
  onMove: (id: number, direction: "up" | "down") => void
}

function ItemCard({ item, isFirst, isLast, onEdit, onDelete, onMove }: ItemCardProps) {
  return (
    <Card className="gap-2 py-3">
      <CardHeader className="pb-0">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-medium">{item.title}</CardTitle>
          <Badge variant={item.published ? "default" : "outline"} className="text-xs">
            {item.published ? "Published" : "Draft"}
          </Badge>
        </div>
      </CardHeader>
      {item.content && (
        <CardContent className="pb-0">
          <p className="line-clamp-2 text-xs text-muted-foreground">{item.content}</p>
        </CardContent>
      )}
      <CardFooter className="justify-end gap-1 pt-0">
        <Button variant="ghost" size="icon" className="size-7" disabled={isFirst} onClick={() => onMove(item.id, "up")} aria-label="Move up">
          <ChevronUpIcon className="size-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="size-7" disabled={isLast} onClick={() => onMove(item.id, "down")} aria-label="Move down">
          <ChevronDownIcon className="size-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="size-7" onClick={() => onEdit(item)} aria-label="Edit item">
          <Edit2Icon className="size-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="size-7 text-destructive hover:text-destructive" onClick={() => onDelete(item)} aria-label="Delete item">
          <Trash2Icon className="size-3.5" />
        </Button>
      </CardFooter>
    </Card>
  )
}

export default function InsideManager({
  property,
  categories,
  items: initialItems,
  selected_category_id,
  selected_render_type,
  urls,
}: Props) {
  const [localItems, setLocalItems] = useState(initialItems)
  const [formOpen, setFormOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<InsideItem | null>(null)
  const [switching, setSwitching] = useState(false)

  useEffect(() => {
    setLocalItems(initialItems)
    setSwitching(false)
  }, [initialItems])

  const selectedCategory = categories.find((c) => c.id === selected_category_id) ?? null

  function selectCategory(cat: InsideCategoryItem) {
    setSwitching(true)
    router.get(urls.index, { category_id: cat.id }, { preserveState: false })
  }

  function openCreate() { setEditingItem(null); setFormOpen(true) }
  function openEdit(item: InsideItem) { setEditingItem(item); setFormOpen(true) }
  function closeForm() { setFormOpen(false); setEditingItem(null) }

  function handleDelete(item: InsideItem) {
    if (!window.confirm(`Delete "${item.title}"?`)) return
    router.delete(item.delete_url, {
      data: { category_id: selected_category_id },
    })
  }

  function handleMove(id: number, direction: "up" | "down") {
    const reordered = swapAdjacent(localItems, id, direction)
    setLocalItems(reordered)
    router.patch(
      urls.reorder,
      { ids: reordered.map((i) => i.id), category_id: selected_category_id },
      { onError: () => setLocalItems(initialItems) },
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">INSIDE Info</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
        </div>
        {selectedCategory && (
          <Button size="sm" onClick={openCreate}>
            <PlusIcon className="mr-1.5 size-4" />
            Add item
          </Button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Left: category list */}
        <div className="w-64 shrink-0">
          <div className="rounded-lg border">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => selectCategory(cat)}
                className={[
                  "flex w-full items-center justify-between border-b px-4 py-3 text-left text-sm last:border-b-0",
                  "hover:bg-muted/50 transition-colors",
                  cat.id === selected_category_id ? "bg-muted font-medium" : "",
                ].join(" ")}
              >
                <span className="flex items-center gap-2">
                  {cat.icon && <PoiIcon name={cat.icon} size={16} />}
                  {cat.name}
                </span>
                {cat.items_count > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {cat.items_count}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Right: items panel */}
        <div className="flex-1">
          {switching ? (
            <div className="grid gap-3">
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>
          ) : !selectedCategory ? (
            <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
              <p className="text-sm">Select a category to manage its items.</p>
            </div>
          ) : localItems.length === 0 ? (
            <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
              <p className="text-sm">No {selectedCategory.name} info yet.</p>
              <Button variant="link" size="sm" onClick={openCreate} className="mt-1">
                Add the first item
              </Button>
            </div>
          ) : (
            <div className="grid gap-3">
              {localItems.map((item, idx) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  isFirst={idx === 0}
                  isLast={idx === localItems.length - 1}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onMove={handleMove}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Sheet open={formOpen} onOpenChange={(open) => !open && closeForm()}>
        <SheetContent className="overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editingItem
                ? `Edit ${selectedCategory?.name ?? ""} item`
                : `Add ${selectedCategory?.name ?? ""} item`}
            </SheetTitle>
          </SheetHeader>
          {formOpen && selectedCategory && (
            <ItemForm
              item={editingItem ?? undefined}
              categoryId={selectedCategory.id}
              renderType={(selected_render_type ?? selectedCategory.render_type) as RenderType}
              createUrl={urls.create}
              onClose={closeForm}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

InsideManager.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
